// ===== 1. AI Soft Approval Service =====
// src/services/aiSoftApprovalService.ts

import { httpRequest } from "../httpRequest";

export interface SoftApprovalIssue {
    category: 'Critical' | 'Warning' | 'Suggestion';
    type: 'Missing Data' | 'Inconsistency' | 'Compliance' | 'Document' | 'Logic';
    title: string;
    description: string;
    impact: string;
    action: string;
    fieldPath?: string;
    documentType?: string;
}

export interface SoftApprovalAnalysis {
    approvalStatus: 'APPROVED' | 'CONDITIONAL' | 'REQUIRES_REVIEW';
    overallConfidence: number;
    completenessScore: number;
    consistencyScore: number;
    complianceScore: number;
    documentationScore: number;

    criticalIssues: SoftApprovalIssue[];
    warnings: SoftApprovalIssue[];
    suggestions: SoftApprovalIssue[];

    nextSteps: string[];
    approvalConditions?: string[];

    summary: {
        totalIssues: number;
        readyForPayment: boolean;
        estimatedProcessingTime: string;
        recommendedActions: string[];
    };
}

export class AISoftApprovalService {

    async getSoftApprovalAnalysis(applicationData: any): Promise<SoftApprovalAnalysis> {
        try {

            const response = await httpRequest('POST', '/construction/AI/generateAISoftApprovalAnalysis', {
                applicationData
            })

            if (response?.status !== 'success') {
                throw new Error(`API request failed: ${response.message}`);
            }

            return this.validateAndSanitize(response?.data);

        } catch (error) {
            console.error('AI Soft Approval Error:', error);
            return this.getFallbackAnalysis(applicationData);
        }
    }

    private validateAndSanitize(analysis: any): SoftApprovalAnalysis {
        if (!analysis.approvalStatus || !analysis.overallConfidence) {
            throw new Error('Invalid AI response format');
        }

        return {
            approvalStatus: analysis.approvalStatus,
            overallConfidence: Math.max(0, Math.min(1, analysis.overallConfidence)),
            completenessScore: Math.max(0, Math.min(1, analysis.completenessScore || 0.7)),
            consistencyScore: Math.max(0, Math.min(1, analysis.consistencyScore || 0.7)),
            complianceScore: Math.max(0, Math.min(1, analysis.complianceScore || 0.7)),
            documentationScore: Math.max(0, Math.min(1, analysis.documentationScore || 0.7)),
            criticalIssues: analysis.criticalIssues || [],
            warnings: analysis.warnings || [],
            suggestions: analysis.suggestions || [],
            nextSteps: analysis.nextSteps || [],
            approvalConditions: analysis.approvalConditions,
            summary: {
                totalIssues: (analysis.criticalIssues?.length || 0) + (analysis.warnings?.length || 0),
                readyForPayment: analysis.approvalStatus === 'APPROVED',
                estimatedProcessingTime: analysis.summary?.estimatedProcessingTime || '2-3 weeks',
                recommendedActions: analysis.summary?.recommendedActions || []
            }
        };
    }

    private getFallbackAnalysis(applicationData: any): SoftApprovalAnalysis {
        const hasBasicInfo = !!(applicationData.ApplicationTitle && applicationData.ApplicantName && applicationData.DescribeUse);
        const hasDocuments = applicationData.DocumentFileUpload?.length > 0;

        return {
            approvalStatus: hasBasicInfo ? 'CONDITIONAL' : 'REQUIRES_REVIEW',
            overallConfidence: hasBasicInfo ? 0.7 : 0.4,
            completenessScore: 0.7,
            consistencyScore: 0.7,
            complianceScore: 0.7,
            documentationScore: hasDocuments ? 0.8 : 0.3,
            criticalIssues: hasBasicInfo ? [] : [{
                category: 'Critical',
                type: 'Missing Data',
                title: 'Incomplete application information',
                description: 'Basic application information is missing',
                impact: 'Cannot proceed without complete information',
                action: 'Complete all required fields'
            }],
            warnings: [{
                category: 'Warning',
                type: 'Document',
                title: 'AI analysis unavailable',
                description: 'Automated analysis could not be completed',
                impact: 'Manual review may be required',
                action: 'Review application manually or contact support'
            }],
            suggestions: [],
            nextSteps: ['Complete basic information', 'Upload required documents'],
            summary: {
                totalIssues: hasBasicInfo ? 1 : 2,
                readyForPayment: false,
                estimatedProcessingTime: '3-4 weeks',
                recommendedActions: ['Complete application', 'Contact support if needed']
            }
        };
    }
}